import { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import TRANSLATIONS from "./translations";
import { ROUTE_MAP } from "./seoMeta";

const LanguageContext = createContext(null);

// Derive language purely from the URL — every page lives at a known path
// in either the CZ root (`/...`) or under the `/en/` prefix. This makes the
// language a first-class part of the URL (needed for hreflang + indexing)
// and removes the need for localStorage/browser-language guessing.
const langFromPath = (pathname) => {
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  return "cs";
};

// Given the current pathname, find the matching route id (if any) so we can
// compute the mirror URL in the other language.
const routeIdFromPath = (pathname) => {
  // Normalize: strip trailing slash for comparison except for root.
  const normalized =
    pathname.length > 1 && pathname.endsWith("/") ? pathname : `${pathname}/`;
  for (const [id, paths] of Object.entries(ROUTE_MAP)) {
    if (paths.cs === normalized || paths.en === normalized) return id;
  }
  return null;
};

export const LanguageProvider = ({ children }) => {
  const { pathname } = useLocation();

  const value = useMemo(() => {
    const lang = langFromPath(pathname);
    const routeId = routeIdFromPath(pathname);
    return {
      lang,
      routeId,
      t: TRANSLATIONS[lang],
      // Compute the mirror URL in the other language. If we cannot match the
      // current route in the route map, fall back to the localized home.
      otherLangPath: routeId
        ? ROUTE_MAP[routeId][lang === "cs" ? "en" : "cs"]
        : lang === "cs"
          ? "/en/"
          : "/",
    };
  }, [pathname]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside <LanguageProvider>");
  return ctx;
};
